import { useEffect } from "react";
import { Link } from "react-router-dom";
import { HelpCircle, ArrowLeft } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const FAQS = [
  {
    q: "What does Al Dente actually mean?",
    a: "Al dente is an Italian phrase meaning to the tooth, describing pasta or rice cooked to be firm when bitten, not soft or mushy. To test it, bite a piece two minutes before the packet time is up — you should see a tiny pale center and feel a clean, slight resistance.",
  },
  {
    q: "What is a Roux and how do I make it?",
    a: "A roux is a smooth paste of equal parts flour and butter cooked together, used to thicken soups and sauces. Melt butter over medium heat, whisk in flour, and cook for at least one minute to remove the raw flour taste.",
  },
  {
    q: "What is the difference between Baking and Roasting?",
    a: "Roasting uses high heat above 200°C to brown and crisp solid foods like meat and vegetables. Baking uses lower heat between 160°C and 190°C to cook foods that transform in the oven, like cakes and bread.",
  },
  {
    q: "What is a Mirepoix?",
    a: "Mirepoix is a French flavor base of finely diced onion, carrot and celery cooked slowly in fat. The classic ratio is 2 parts onion to 1 part each of carrot and celery. It forms the invisible flavor foundation of most soups, stews and braises.",
  },
  {
    q: "What does it mean to Fold an ingredient?",
    a: "Folding is a gentle technique for combining a light ingredient into a heavier mixture without losing air. Use a rubber spatula, cut straight down through the center, sweep across the bottom and fold up and over. Repeat until just combined.",
  },
  {
    q: "What is Swicy food?",
    a: "Swicy combines sweet and spicy flavors in one dish. Try hot honey on fried chicken, chilli flakes on roasted sweet potato, or cayenne in a chocolate dessert.",
  },
  {
    q: "What does Blanching mean?",
    a: "Blanching means briefly boiling a vegetable then plunging it immediately into ice water to stop the cooking. This locks in color, flavor and texture, and is essential before freezing fresh vegetables.",
  },
  {
    q: "What does it mean to Deglaze a pan?",
    a: "Deglazing means adding liquid to a hot pan after searing meat to loosen the caramelized bits stuck to the bottom. Those bits, called fond, dissolve into the liquid and form the base of a rich, flavorful sauce.",
  },
  {
    q: "What is the difference between a Convection and a Conventional oven?",
    a: "A convection oven circulates hot air with a fan, cooking food roughly 25 percent faster and more evenly. A conventional oven uses stationary heat. When using convection, lower the recipe temperature by about 20°C.",
  },
  {
    q: "What is the ratio for substituting Fresh Herbs with Dried Herbs?",
    a: "Use three parts fresh to one part dried. Dried herbs are far more concentrated in flavor. One tablespoon of fresh rosemary equals one teaspoon dried.",
  },
  {
    q: "What is the difference between Dutch-process and Natural Cocoa Powder?",
    a: "Natural cocoa is acidic and must be paired with baking soda. Dutch-process cocoa is neutralised and requires baking powder. They are not interchangeable in baking recipes.",
  },
  {
    q: "Why should I use a Digital Kitchen Scale instead of Measuring Cups?",
    a: "A scale measures mass precisely. A cup of flour can weigh anywhere from 120 to 160 grams depending on how it is packed. A scale eliminates that error entirely.",
  },
  {
    q: "What is the difference between Chopping, Dicing and Mincing?",
    a: "Chopping means cutting food into rough, irregular pieces. Dicing means cutting into small, uniform cubes for even cooking. Mincing means cutting as finely as possible, almost to a paste. The finer the cut, the faster and more evenly the ingredient cooks and releases flavor.",
  },
  {
    q: "What is a Julienne cut and when do I use it?",
    a: "Julienne means cutting food into long, thin matchstick strips roughly 3mm wide. It is used for stir fries, salads and garnishes where you want fast, even cooking or an elegant presentation.",
  },
  {
    q: "What does Rough Chop mean in a recipe?",
    a: "Rough chop means cutting ingredients into large, uneven pieces without worrying about precision. It is used when the food will be blended, strained or cooked down, so the exact size does not matter.",
  },
  {
    q: "What is the difference between Slicing and Chiffonade?",
    a: "Slicing means cutting food into flat, even pieces. Chiffonade is a specific technique for leafy herbs or greens — stack the leaves, roll them tightly, then slice across into fine ribbons.",
  },
];

export default function FAQ() {
  // Inject FAQPage JSON-LD schema into <head> so AI search engines can index it.
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "faq-jsonld";
    el.textContent = JSON.stringify(schema);
    // remove any prior one before appending
    document.getElementById("faq-jsonld")?.remove();
    document.head.appendChild(el);
    // also set a helpful page title
    const prevTitle = document.title;
    document.title = "Your Food Questions, Answered — Bite & Banter";
    return () => {
      document.getElementById("faq-jsonld")?.remove();
      document.title = prevTitle;
    };
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-20" data-testid="faq-page">
      <Link
        to="/"
        className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-widest opacity-70 hover-underline mb-8"
        data-testid="faq-back-home"
      >
        <ArrowLeft size={14} /> Back to home
      </Link>

      <div className="mb-12">
        <div className="font-ui text-xs uppercase tracking-[0.3em] opacity-60 mb-3 flex items-center gap-2">
          <HelpCircle size={14} /> Common questions
        </div>
        <h1 className="font-display text-5xl md:text-7xl leading-[0.95]" data-testid="faq-title">
          Your Food Questions, Answered.
        </h1>
        <p className="font-body text-lg md:text-xl leading-relaxed mt-6 max-w-2xl opacity-80">
          Sixteen of the questions we hear most in the kitchen — with clear, plain-English answers.
          Click any question to open its answer.
        </p>
      </div>

      <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
        {FAQS.map((item, idx) => (
          <AccordionItem
            key={idx}
            value={`item-${idx}`}
            className="border-b border-black/15"
            data-testid={`faq-item-${idx}`}
          >
            <AccordionTrigger
              className="font-display text-2xl md:text-3xl text-left py-6 hover:no-underline"
              data-testid={`faq-question-${idx}`}
            >
              {item.q}
            </AccordionTrigger>
            <AccordionContent
              className="font-body text-base md:text-lg leading-relaxed pb-6 pr-4"
              data-testid={`faq-answer-${idx}`}
            >
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="mt-16 border-t border-black/15 pt-8">
        <p className="font-display italic text-xl md:text-2xl opacity-90">
          — Have another question? Search the glossary; if we haven&rsquo;t defined it yet, Bite &amp; Banter will write one for you on the spot.
        </p>
        <Link
          to="/glossary"
          className="btn-primary mt-6 inline-flex"
          data-testid="faq-cta-glossary"
        >
          Open the glossary
        </Link>
      </div>
    </div>
  );
}
